import { getParisDateKey } from "./parisDateTime";

export type FeedbackRow = {
  id: string;
  case_id: string;
  sent_at: string | null;
  express_at: string | null;
  detailed_at: string | null;
  overall: number | null;
  sales: number | null;
  venue: number | null;
  nps: number | null;
  activities: string[];
  cross_selling_optin: boolean;
  catering: number | null;
  catering_na: boolean;
  improvement: string | null;
  testimonial_consent: boolean;
  consent_at: string | null;
  followup_status: "open" | "in_progress" | "resolved";
  staff_note: string | null;
  snapshot: {
    client_first_name: string | null;
    client_last_name: string | null;
    client_email: string | null;
    company_name: string | null;
    center: string | null;
    event_date: string;
    activities: string[];
    title: string;
  };
};
export const feedbackState = (r: FeedbackRow) =>
  r.detailed_at
    ? "Avis complet"
    : r.express_at
      ? "Avis express"
      : r.sent_at
        ? "En attente"
        : "Non envoyé";
export const hasAlert = (r: FeedbackRow) =>
  !!r.express_at && r.overall != null && r.overall <= 2 && r.followup_status !== "resolved";
export function feedbackMetrics(rows: FeedbackRow[]) {
  const answered = rows.filter((r) => r.express_at);
  // Une réponse prouve la réception, même si l'accusé du fournisseur n'a pas été enregistré.
  const sent = rows.filter((r) => r.sent_at || r.express_at).length;
  const average = (key: "sales" | "venue") => {
    const values = answered.flatMap((r) => (r[key] == null ? [] : [r[key]!]));
    return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
  };
  const npsRows = answered.filter((r) => r.nps != null);
  const promoters = npsRows.filter((r) => r.nps! >= 9).length;
  const detractors = npsRows.filter((r) => r.nps! <= 6).length;
  const positives = answered.filter((r) => r.overall != null && r.overall >= 4).length;
  return {
    sent,
    answers: answered.length,
    responseRate: sent ? (answered.length / sent) * 100 : null,
    satisfaction: answered.length ? (positives / answered.length) * 100 : null,
    sales: average("sales"),
    venue: average("venue"),
    nps: npsRows.length ? ((promoters - detractors) / npsRows.length) * 100 : null,
    npsCount: npsRows.length,
    promoters,
    detractors,
    interests: answered.filter((r) => r.cross_selling_optin).length,
    alerts: rows.filter(hasAlert).length,
    distribution: [5, 4, 3, 2, 1].map((score) => ({
      score,
      count: answered.filter((r) => r.overall === score).length,
    })),
  };
}
export function filterFeedback(rows: FeedbackRow[], month: string, center: string) {
  return rows.filter(
    (r) =>
      (!month || getParisDateKey(new Date(r.snapshot.event_date)).startsWith(month)) &&
      (!center || r.snapshot.center === center),
  );
}
const csvCell = (value: unknown) => {
  let s = String(value ?? "");
  if (/^[\s]*[=+\-@\t\r]/.test(s)) s = "'" + s;
  return '"' + s.replace(/"/g, '""') + '"';
};
export function feedbackCsv(rows: FeedbackRow[]) {
  const header = [
    "Dossier",
    "Entreprise",
    "Centre",
    "Date événement (Paris)",
    "Statut",
    "Note globale /5",
    "Commercial /5",
    "Terrain /5",
    "NPS /10",
    "Demande autres centres",
    "Activités appréciées",
    "Restauration /5",
    "Amélioration",
    "Autorisation témoignage",
    "Consentement enregistré",
    "Suivi",
  ];
  return (
    "\uFEFF" +
    [
      header,
      ...rows.map((r) => [
        r.snapshot.title,
        r.snapshot.company_name,
        r.snapshot.center,
        getParisDateKey(new Date(r.snapshot.event_date)),
        feedbackState(r),
        r.overall,
        r.sales,
        r.venue,
        r.nps,
        r.cross_selling_optin ? "Oui" : "Non",
        r.activities.join(", "),
        r.catering_na ? "Non concerné" : r.catering,
        r.improvement,
        r.testimonial_consent ? "Oui" : "Non",
        r.consent_at,
        r.followup_status,
      ]),
    ]
      .map((line) => line.map(csvCell).join(";"))
      .join("\r\n")
  );
}
